import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { G21Mark } from "@/components/landing/G21Monogram";
import { useT, useLang } from "@/i18n/LanguageProvider";

/**
 * SiteHeader – sticky, glass-morphism multi-page navigation introduced
 * with the May 2026 redesign. Replaces the anchor-scroll Navbar.jsx for
 * the new multi-page architecture.
 */
export const SiteHeader = () => {
    const t = useT();
    const { lang, setLang } = useLang();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    // Close mobile drawer on route change
    useEffect(() => { setOpen(false); }, [pathname]);

    // Scrolled state for subtle shadow
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const NAV = [
        { to: "/wohnungen",       label: t({ de: "Wohnungen",        en: "Apartments" }) },
        { to: "/architektur",     label: t({ de: "Architektur",      en: "Architecture" }) },
        { to: "/investor",        label: t({ de: "Kapitalanlage",    en: "Investment" }) },
        { to: "/vermoegensaufbau",label: t({ de: "Vermögensaufbau",  en: "Wealth plan" }) },
        { to: "/kontakt",         label: t({ de: "Kontakt",          en: "Contact" }) },
    ];

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300
                ${scrolled
                    ? "bg-linen/85 backdrop-blur-xl border-b border-black/5 shadow-[0_4px_24px_rgba(61,90,75,0.04)]"
                    : "bg-linen/60 backdrop-blur-md border-b border-transparent"}`}
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
                <div className="flex items-center justify-between h-16 sm:h-20">
                    {/* Logo */}
                    <Link
                        to="/"
                        className="flex items-center gap-3 group"
                        data-testid="site-header-logo"
                    >
                        <G21Mark size={34} className="text-sage-500" />
                        <div className="hidden sm:block">
                            <div className="font-display font-bold text-sage-500 text-base tracking-tight leading-none">
                                G21 Wiesbaden
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-500/60 mt-0.5">
                                {t({ de: "Neubau · Biebrich", en: "New build · Biebrich" })}
                            </div>
                        </div>
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    `px-3 py-2 text-sm font-body transition-colors relative ` +
                                    (isActive
                                        ? "text-sage-500 font-medium"
                                        : "text-sage-500/70 hover:text-sage-500")
                                }
                                data-testid={`nav-link-${item.to.replace("/", "")}`}
                            >
                                {({ isActive }) => (
                                    <>
                                        {item.label}
                                        {isActive && (
                                            <span className="absolute left-3 right-3 -bottom-px h-px bg-terracotta-300" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Right actions */}
                    <div className="flex items-center gap-2">
                        {/* Language toggle */}
                        <div className="hidden md:flex items-center text-xs font-body font-medium">
                            <button
                                onClick={() => setLang("de")}
                                className={`px-2 py-1 rounded-full transition-colors ${
                                    lang === "de"
                                        ? "text-sage-500 bg-mint-200/70"
                                        : "text-sage-500/50 hover:text-sage-500"
                                }`}
                                aria-pressed={lang === "de"}
                                data-testid="lang-de"
                            >DE</button>
                            <span className="text-sage-500/30 px-0.5">|</span>
                            <button
                                onClick={() => setLang("en")}
                                className={`px-2 py-1 rounded-full transition-colors ${
                                    lang === "en"
                                        ? "text-sage-500 bg-mint-200/70"
                                        : "text-sage-500/50 hover:text-sage-500"
                                }`}
                                aria-pressed={lang === "en"}
                                data-testid="lang-en"
                            >EN</button>
                        </div>

                        <Link
                            to="/wohnungen"
                            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sage-500 text-white text-sm font-body font-medium hover:bg-sage-600 hover:-translate-y-0.5 active:translate-y-0 transition-all shadow-[0_4px_14px_rgba(61,90,75,0.18)]"
                            data-testid="site-header-cta"
                        >
                            {t({ de: "Wohnungen ansehen", en: "View apartments" })}
                            <ArrowRight size={15} />
                        </Link>

                        {/* Mobile hamburger */}
                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="lg:hidden p-2 rounded-full text-sage-500 hover:bg-mint-100"
                            aria-label="Open menu"
                            data-testid="mobile-menu-trigger"
                        >
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    className="lg:hidden fixed inset-0 z-[60] bg-linen flex flex-col"
                    data-testid="mobile-drawer"
                    role="dialog"
                    aria-modal="true"
                >
                    <div className="flex items-center justify-between h-16 px-6 border-b border-black/5">
                        <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
                            <G21Mark size={32} className="text-sage-500" />
                            <span className="font-display font-bold text-sage-500">G21</span>
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="p-2 rounded-full text-sage-500 hover:bg-mint-100"
                            aria-label="Close menu"
                            data-testid="mobile-menu-close"
                        >
                            <X size={22} />
                        </button>
                    </div>
                    <nav className="flex-1 overflow-y-auto px-6 py-8">
                        <ul className="space-y-1">
                            {NAV.map((item) => (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        className={({ isActive }) =>
                                            "block px-4 py-4 rounded-2xl font-display font-bold text-2xl tracking-tight transition-colors " +
                                            (isActive
                                                ? "bg-mint-200 text-sage-500"
                                                : "text-sage-500/85 hover:bg-mint-100")
                                        }
                                        data-testid={`mobile-nav-${item.to.replace("/", "")}`}
                                    >
                                        {item.label}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-10 pt-6 border-t border-black/5">
                            <div className="flex items-center gap-3 text-sm font-body font-medium">
                                <button
                                    onClick={() => setLang("de")}
                                    className={`px-4 py-2 rounded-full ${
                                        lang === "de" ? "bg-sage-500 text-white" : "text-sage-500/70 hover:bg-mint-100"
                                    }`}
                                >DE</button>
                                <button
                                    onClick={() => setLang("en")}
                                    className={`px-4 py-2 rounded-full ${
                                        lang === "en" ? "bg-sage-500 text-white" : "text-sage-500/70 hover:bg-mint-100"
                                    }`}
                                >EN</button>
                            </div>
                        </div>
                    </nav>
                    <div className="p-6 border-t border-black/5">
                        <Link
                            to="/kontakt"
                            onClick={() => setOpen(false)}
                            className="inline-flex w-full items-center justify-center gap-2 px-6 py-4 rounded-full bg-sage-500 text-white font-body font-medium"
                        >
                            {t({ de: "Kontakt aufnehmen", en: "Get in touch" })}
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};
